// Console terminal component. One instance per device; the workspace switches
// between them with tabs. Knows nothing about Cisco or Windows — it drives any
// shell exposing prompt() / exec() / help() / complete() through the runtime.

import { h } from './h.js';
import { dev, isHost } from '../engine/net/sim.js';
import { play } from './sound.js';

const speed = () => (typeof window !== 'undefined' && window.__NELL_FAST__ ? 0 : 0.4);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function createTerminal(rt, id) {
  const d = dev(rt.net, id);
  const ios = !isHost(d);
  const out = h('div', { class: 'term-out', 'aria-live': 'polite' });
  const promptEl = h('span', { class: 'term-prompt' });
  const input = h('input', {
    class: 'term-input', spellcheck: 'false', autocomplete: 'off', autocapitalize: 'off',
    'aria-label': `${id} command line`, 'data-testid': `term-input-${id}`,
  });
  const line = h('div', { class: 'term-line' }, promptEl, input);
  const body = h('div', { class: 'term-body', onclick: () => { if (!window.getSelection()?.toString()) input.focus(); } }, out, line);
  const el = h('div', { class: `term ${ios ? 'term-ios' : 'term-win'}`, 'data-dev': id },
    h('div', { class: 'term-head' },
      h('span', { class: 'term-dot' }), h('span', { class: 'term-dot' }), h('span', { class: 'term-dot' }),
      h('span', { class: 'term-title' }, ios ? `${d.hostname} — Console (9600 8N1)` : `${d.hostname} — Windows PowerShell`),
      h('span', { class: 'term-keys' }, ios ? 'Tab complete · ? help · ↑↓ history · Ctrl+C abort · Ctrl+Z end' : '↑↓ history · help · Ctrl+C abort')),
    body);

  let busy = false, abort = false, histIdx = null;
  const shell = () => rt.shell(id);

  const paint = (l) => {
    const div = h('div', { class: `tl ${l.cls || ''}` }, l.text === '' ? ' ' : l.text);
    out.append(div);
    return div;
  };
  const scrollDown = () => { body.scrollTop = body.scrollHeight; };
  // Password prompts (enable secret, console login, SSH/Telnet) are not echoed.
  const isSecret = () => !!shell().isSecret?.();
  const refreshPrompt = () => { promptEl.textContent = shell().prompt(); input.type = isSecret() ? 'password' : 'text'; };

  function renderAll() {
    out.textContent = '';
    for (const l of rt.scrollback(id)) paint(l);
    refreshPrompt();
    scrollDown();
  }

  async function print(lines) {
    for (const l of lines) {
      if (abort) break;
      const ms = Math.min(1400, (l.delay || 0) * speed());
      if (l.progressive && speed()) {
        const div = paint({ text: '' });
        for (const ch of l.text) {
          if (abort) break;
          div.textContent += ch;
          scrollDown();
          await sleep(Math.min(260, ms / l.text.length));
        }
        rt.appendScroll(id, [{ text: l.text }]);
        continue;
      }
      if (ms) await sleep(ms);
      if (abort) break;
      paint(l);
      rt.appendScroll(id, [l]);
      scrollDown();
    }
  }

  async function submit(text) {
    busy = true;
    line.classList.add('busy');
    const echo = { text: shell().prompt() + (isSecret() ? '' : text), cls: 'cmd' };
    paint(echo);
    rt.appendScroll(id, [echo]);
    scrollDown();
    const r = rt.exec(id, text);
    if (r.clear) { out.textContent = ''; rt.clearScroll(id); }
    abort = false;
    await print(r.lines);
    if (abort) { paint({ text: '^C' }); rt.appendScroll(id, [{ text: '^C' }]); }
    abort = false;
    busy = false;
    line.classList.remove('busy');
    refreshPrompt();
    rt.save();
    scrollDown();
    input.focus();
  }

  input.addEventListener('keydown', (e) => {
    const hist = shell().state.history;
    if (e.ctrlKey && (e.key === 'c' || e.key === 'C')) {
      if (busy) { abort = true; e.preventDefault(); return; }
      if (!window.getSelection()?.toString()) { e.preventDefault(); paint({ text: shell().prompt() + input.value + '^C' }); input.value = ''; scrollDown(); }
      return;
    }
    if (busy) { e.preventDefault(); return; }
    if (e.key.length === 1 || e.key === 'Enter' || e.key === 'Backspace') play('tick'); // optional typing sound (off by default)
    if (e.key === 'Enter') {
      e.preventDefault();
      const v = input.value;
      input.value = '';
      histIdx = null;
      submit(v);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!hist.length || isSecret()) return;
      histIdx = histIdx === null ? hist.length - 1 : Math.max(0, histIdx - 1);
      input.value = hist[histIdx];
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (histIdx === null) return;
      histIdx++;
      if (histIdx >= hist.length) { histIdx = null; input.value = ''; } else input.value = hist[histIdx];
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (!ios) return;
      const c = shell().complete(input.value);
      if (c) input.value = c;
    } else if (e.key === '?' && ios && !shell().state.ask) {
      e.preventDefault();
      const cur = input.value;
      const echo = { text: shell().prompt() + cur + '?', cls: 'cmd' };
      const lines = shell().help(cur).map((text) => ({ text }));
      paint(echo); lines.forEach(paint);
      rt.appendScroll(id, [echo, ...lines]);
      rt.action('help', { device: id, line: cur });
      scrollDown();
    } else if (e.ctrlKey && (e.key === 'z' || e.key === 'Z') && ios) {
      e.preventDefault();
      if (shell().state.mode.startsWith('config')) { input.value = ''; submit('end'); }
    }
  });

  renderAll();
  return {
    el, id,
    focus: () => input.focus(),
    refresh: renderAll,
    /** Used by tests / guided steps to type a command programmatically. */
    run: (text) => submit(text),
    isBusy: () => busy,
  };
}
